// src/features/sales/DistinctSalesList.js
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import {
  useGetSalesWithDistinctQuery,
  makeSaleDistinctSelectors,
} from "./salesApiSlice";

import DistinctSaleTable from "./components/DistinctSaleTable";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 200];

export default function DistinctSalesList() {
    // Local filters
    const [q, setQ] = useState("");
    const [yearMonth, setYearMonth] = useState(""); // format: "2024-01"
    const [sortBy, setSortBy] = useState("yearMonth");   // default for summary
    const [order, setOrder] = useState("asc");           // ascending for time feels natural
    const [page, setPage] = useState(1);                 // 1-based
    const [limit, setLimit] = useState(10);

    // Build stable params for the *backend* query.
    // Distinct endpoint does NOT paginate on server; we only send q (search).
    const queryArgs = useMemo(() => {
    const p = {};

    if (q.trim()) p.q = q.trim();

    if (yearMonth) {
        // yearMonth is like "2024-01"
        const [y, m] = yearMonth.split("-");
        const year = Number(y);
        const month = Number(m);

        if (!Number.isNaN(year) && !Number.isNaN(month)) {
        // from: first day of month
        const from = new Date(year, month - 1, 1);
        // to: last day of month (next month day 0)
        const to = new Date(year, month, 0);

        p.from = from.toISOString().slice(0, 10); // "YYYY-MM-DD"
        p.to = to.toISOString().slice(0, 10);
        }
    }

    return p;
    }, [q, yearMonth]);


    // Fetch all distinct/summary rows for given filters from backend
    const {
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetSalesWithDistinctQuery(queryArgs, {
        pollingInterval: 300_000,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
    });

    // Arg-aware selectors for THIS query args (summary endpoint)
    const {
        selectAllSalesSummary,
        selectSummaryTotal,
    } = useMemo(() => makeSaleDistinctSelectors(queryArgs), [queryArgs]);

    // Normalized summary rows + total from the store
    const allRows = useSelector(selectAllSalesSummary);
    const totalFromStore = useSelector(selectSummaryTotal);

    // Client-side sort
    const sortedRows = useMemo(() => {
        const rows = [...allRows];

        rows.sort((a, b) => {
        const dir = order === "desc" ? -1 : 1;

        switch (sortBy) {
            case "main_company": {
            const av = a.main_company ?? "";
            const bv = b.main_company ?? "";
            return av.localeCompare(bv) * dir;
            }
            case "net_sales": {
            const av = Number(a.net_sales) || 0;
            const bv = Number(b.net_sales) || 0;
            return av === bv ? 0 : av < bv ? -1 * dir : 1 * dir;
            }
            case "debet": {
            const av = Number(a.debet) || 0;
            const bv = Number(b.debet) || 0;
            return av === bv ? 0 : av < bv ? -1 * dir : 1 * dir;
            }
            case "yearMonth":
            default: {
            // yearMonth is string "YYYYMM" so lexicographic sort works
            const av = a.yearMonth ?? "";
            const bv = b.yearMonth ?? "";
            return av.localeCompare(bv) * dir;
            }
        }
        });

        return rows;
    }, [allRows, sortBy, order]);

    const total = sortedRows.length;

    // Client-side pagination
    const pagedRows = useMemo(() => {
        const start = (page - 1) * limit;
        const end = start + limit;
        return sortedRows.slice(start, end);
    }, [sortedRows, page, limit]);

    // Paging math
    const totalPages = Math.max(1, Math.ceil((total || 0) / limit));
    const from = total === 0 ? 0 : (page - 1) * limit + 1;
    const to = Math.min(page * limit, total || 0);

    const pageIsFull = pagedRows.length >= limit;
    const canPrev = page > 1;
    const canNext = totalPages > 1 ? page < totalPages : pageIsFull;

    const onPrev = () => { if (canPrev) setPage((p) => p - 1); };
    const onNext = () => { if (canNext) setPage((p) => p + 1); };

    const onPageSizeChange = (e) => {
        const newLimit = Number(e.target.value);
        setLimit(newLimit);
        setPage(1); // reset when size changes
    };

    const loading = isLoading || isFetching;

    return (
        <section className="p-6 space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
            <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">
                Sales Summary
            </h1>
            <p className="text-sm text-gray-500">
                Aggregated sales by main company and month (YearMonth).
            </p>
            </div>
            {/* This is a summary view; usually no "New" button here.
                If you still want it, you can keep the link below. */}
            {/* <Link
            to="/dash/sales/new"
            className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800"
            >
            New Sale
            </Link> */}
        </header>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4">
            {/* Search by company or yearMonth (front-end & back-end) */}
            <input
                value={q}
                onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                }}
                placeholder="Search company / YearMonth…"
                className="w-64 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            />

            {/* YearMonth filter */}
            <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Month:</label>
                <input
                type="month"
                value={yearMonth}
                onChange={(e) => {
                    setYearMonth(e.target.value);
                    setPage(1);
                }}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
                />
            </div>

            <div className="ml-auto flex items-center gap-2">
            {/* Sort field */}
            <select
                value={sortBy}
                onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
                }}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
                <option value="yearMonth">YearMonth</option>
                <option value="main_company">Main Company</option>
                <option value="net_sales">Net Sales</option>
                <option value="debet">Debet</option>
            </select>

            {/* Sort order */}
            <select
                value={order}
                onChange={(e) => {
                setOrder(e.target.value);
                setPage(1);
                }}
                className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
            >
                <option value="asc">Asc</option>
                <option value="desc">Desc</option>
            </select>
            </div>
        </div>

        {/* Error state */}
        {isError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error?.data?.message || "Failed to load sales summary."}
            </div>
        )}

        {/* Table */}
        <DistinctSaleTable sales={pagedRows} loading={loading} />

        {/* Pagination footer */}
        <div className="flex items-center justify-between text-sm text-neutral-600">
            <div>
            {loading
                ? "Loading…"
                : `${from}–${to} of ${total?.toLocaleString?.() ?? total}`}
            </div>

            <div className="flex items-center gap-3">
            {/* Page size */}
            <label className="text-sm text-neutral-500 mr-1">Rows:</label>
            <select
                value={limit}
                onChange={onPageSizeChange}
                className="bg-neutral-900 text-neutral-100 border border-neutral-700 rounded-xl px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
                ))}
            </select>

            {/* Prev/Next */}
            <div className="flex items-center gap-1">
                <button
                onClick={onPrev}
                disabled={!canPrev}
                aria-label="Previous page"
                title="Previous"
                className={`pagination-btn h-9 w-9 rounded-full border border-neutral-700 flex items-center justify-center transition
                    outline-none focus:ring-2 focus:ring-sky-500/40 focus:ring-offset-0
                    ${canPrev ? "hover:bg-neutral-800 active:scale-[0.98]" : "opacity-40 cursor-not-allowed"}`}
                >
                <ChevronLeftIcon className="h-4 w-4 text-neutral-700" />
                </button>

                <button
                onClick={onNext}
                disabled={!canNext}
                aria-label="Next page"
                title="Next"
                className={`pagination-btn h-9 w-9 rounded-full border border-neutral-700 flex items-center justify-center transition
                    outline-none focus:ring-2 focus:ring-sky-500/40 focus:ring-offset-0
                    ${canNext ? "hover:bg-neutral-800 active:scale-[0.98]" : "opacity-40 cursor-not-allowed"}`}
                >
                <ChevronRightIcon className="h-4 w-4 text-neutral-700" />
                </button>
            </div>
            </div>
        </div>
        </section>
    );
}
